import { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { startCase } from "lodash";
import {
  Controller,
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";
import type { RefinementCtx } from "zod";

import {
  Alert,
  Button,
  Icon,
  Input,
  Link,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetAllRelationships,
  useGetCurrencyCloudBeneficiaryRequiredDetails,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  type CreateCurrencyCloudBeneficiaryRequest,
  type CurrencyCloudBeneficiary,
  createCurrencyCloudAccount,
  createCurrencyCloudBeneficiary,
} from "~/v2/api/rest/currencyCloud";
import { FullScreenSpinner } from "~/v2/components/common";
import { Field } from "~/v2/components/form/field";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FAQ, type FAQItem } from "../../../../../components/common/FAQ";
import { Layout } from "../../common/Layout";
import { useGetPaths } from "../useGetPath";
import { type CurrencyCloudSchema, currencyCloudSchema } from "./schema";

const SUPPORTED_REGIONS = [
  "Austria",
  "Belgium",
  "Bulgaria",
  "Canada",
  "Croatia",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Netherlands",
  "Norway",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "United Kingdom",
  "Vatican",
];

const GENERAL_CURRENCYCLOUD_FAQ: FAQItem[] = [
  {
    question: "Does Alpaca charge for Local Currency Transfers?",
    answer: (
      <>
        Alpaca charges a 1.5% transaction (maximum $40) fee for both deposits
        and withdrawals.
      </>
    ),
  },
  {
    question: "Is my region supported?",
    answer: (
      <>
        The following regions support local currency transfer:
        <ul className="ml-6 list-disc pt-2">
          {SUPPORTED_REGIONS.map((region) => (
            <li key={region}>{region}</li>
          ))}
        </ul>
      </>
    ),
  },
  {
    question: "What if my region is not supported?",
    answer: (
      <>
        You can still send your funds through a traditional international wire
        transfer.
      </>
    ),
  },
  {
    question: "How do I delink my current bank account?",
    answer: (
      <>
        We do not currently support delinking through the dashboard, but our
        team can complete this for you. Please reach out to Support via chat or
        by email at{" "}
        <Link href="mailto:support@alpaca.markets">support@alpaca.markets</Link>
        .
      </>
    ),
  },
  {
    question: "Does it work with my Revolut account?",
    answer: (
      <>
        Revolut is not currently supported by CurrencyCloud. You can fund your
        account using a traditional wire transfer instead.
      </>
    ),
  },
];

export const CURRENCYCLOUD_DEPOSIT_FAQ: FAQItem[] = [
  {
    question: "How does Local Currency Transfer work?",
    answer: (
      <>
        Alpaca partners with CurrencyCloud, Visa&#39;s international payment
        solution, to enable faster and cheaper international transfers than
        traditional wires.
        <br />
        <br />
        You can transfer funds from your local bank to Alpaca&#39;s local bank
        account in your country. Once received, your Alpaca account is credited
        in USD - faster and more secure than traditional international wires.
      </>
    ),
  },
  ...GENERAL_CURRENCYCLOUD_FAQ,
];

export const CURRENCYCLOUD_WITHDRAW_FAQ: FAQItem[] = [
  {
    question: "How does Local Currency Transfer work?",
    answer: (
      <>
        Alpaca partners with CurrencyCloud, Visa&#39;s international payment
        solution, to enable faster and cheaper international transfers than
        traditional wires.
        <br />
        <br />
        You can withdraw funds from your Alpaca account to your local bank
        through Alpaca&#39;s local bank account in your country. Once processed,
        the funds are delivered to your bank in your local currency—faster and
        more secure than traditional international wires.
      </>
    ),
  },
  ...GENERAL_CURRENCYCLOUD_FAQ,
];

const LABELS: CurrencyCloudBeneficiary = {
  account_number: "Account Number",
  iban: "IBAN",
  bic_swift: "BIC/SWIFT code",
  sort_code: "Sort Code",
  aba: "ABA Routing Number",
  bsb_code: "Bank State Branch (BSB) Code",
  bank_code: "Bank Code",
  branch_code: "Branch Code",
  clabe: "CLABE (Clave Bancaria Estandarizada)",
  cnaps: "CNAPS (China National Advanced Payment System) ",
  ifsc: "IFSC (Indian Financial System Code)",
};

const LinkCurrencyCloudPage = () => {
  const history = useHistory();

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const { currencyCloud } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  const { data: requiredDetailsData } =
    useGetCurrencyCloudBeneficiaryRequiredDetails({
      accountID: currentAccount?.id || "",
      options: {
        enabled: !!currentAccount?.id,
      },
    });

  const requiredDetails = useMemo(
    () => requiredDetailsData?.required_details?.at(0),
    [requiredDetailsData?.required_details],
  );

  const form = useForm<CurrencyCloudSchema>({
    resolver: zodResolver(
      currencyCloudSchema.superRefine(
        (fields: CurrencyCloudSchema, refinementContext: RefinementCtx) => {
          if (!requiredDetails) {
            return;
          }

          Object.entries(requiredDetails).forEach(([field, regex]) => {
            const value = fields[field as keyof typeof fields] ?? "";
            const fieldRegex = regex ? new RegExp(regex) : null;

            if (!fieldRegex) {
              return;
            }

            const isInvalid = !fieldRegex.test(value);

            if (isInvalid) {
              refinementContext.addIssue({
                path: [field],
                code: "custom",
                message: `${startCase(field)} must match (${fieldRegex})`,
              });
            }
          });
        },
      ),
    ),
    mode: "onChange",
  });

  const paths = useGetPaths("CurrencyCloud");

  const mutate = useMutationRequest(
    async (data: CreateCurrencyCloudBeneficiaryRequest) => {
      await createCurrencyCloudAccount(currentAccount?.id || "");
      await createCurrencyCloudBeneficiary(currentAccount?.id || "", data);
    },
    {
      onSuccess: () => {
        toastSuccess("Account linked successfully");
        currencyCloud.refetchAccount();
        currencyCloud.refetchStatus();
        history.push(paths.next);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`CurrencyCloud error: ${errorMessage}`);
      },
    },
  );

  const onBackClicked = () => history.push(paths.back);

  const onSubmit: SubmitHandler<CurrencyCloudSchema> = (data) => {
    mutate.mutate(data as CurrencyCloudBeneficiary);
  };

  const onSubmitError: SubmitErrorHandler<CurrencyCloudBeneficiary> = (_e) => {
    toastError("Invalid form, please check the form for errors");
  };

  if (!requiredDetails) {
    return <FullScreenSpinner />;
  }

  return (
    <Layout
      title="Link CurrencyCloud"
      subtitle="Link your bank via CurrencyCloud"
      faq={<FAQ faq={GENERAL_CURRENCYCLOUD_FAQ} />}
    >
      <Button onClick={onBackClicked} variant="secondary" className="mb-8">
        <Icon name="ArrowLeftCircle" className="mr-2" /> Back
      </Button>
      <Text className="mb-4 w-full max-w-lg">
        Alpaca has partnered with CurrencyCloud to facilitate international
        deposits and withdrawals, faster and cheaper than traditional wire.
      </Text>
      <div className="w-full max-w-lg rounded border border-gray-200 p-4">
        <form onSubmit={form.handleSubmit(onSubmit, onSubmitError)}>
          <Text className="pb-4 font-semibold">Bank Information</Text>
          {requiredDetails?.iban ? (
            <Alert
              variant="info"
              message={
                <span>
                  Please ensure your IBAN code is matching with your country of
                  tax residence.{" "}
                  <Link href="https://support.currencycloud.com/hc/en-gb/articles/360017418760-IBAN-Formats-by-Country">
                    For more information.
                  </Link>
                </span>
              }
              className="mb-8"
            />
          ) : null}

          {Object.entries(requiredDetails)
            // Only show required fields, not are all applicable
            .filter(([_field, regex]) => !!regex)
            .map(([field]) => {
              const label =
                LABELS[field as keyof typeof LABELS] ?? startCase(field);
              const isInvalid =
                !!form.formState.errors[
                  field as keyof CurrencyCloudBeneficiary
                ];
              const errorMessage =
                form.formState.errors[field as keyof CurrencyCloudBeneficiary]
                  ?.message ?? "";

              return (
                <div key={field} className="mb-2">
                  <Field
                    label={label}
                    required={true}
                    error={isInvalid}
                    errorMsg={errorMessage}
                  >
                    <Controller
                      name={field as keyof CurrencyCloudBeneficiary}
                      control={form.control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder="eg. HU93116000060000000012345676"
                        />
                      )}
                    />
                  </Field>
                </div>
              );
            })}

          <Button type="submit" className="mt-4 w-full">
            Submit
          </Button>
        </form>
      </div>
    </Layout>
  );
};

export default LinkCurrencyCloudPage;
